import type { PlanTier } from './auth';
import { PLAN_PRICING_AED } from './billing-pricing';

// ─── Plan Hierarchy ──────────────────────────────────────────────────────────

export const PLAN_ORDER: Record<PlanTier, number> = {
  free: 0,
  starter: 1,
  menu: 2,
  operate: 3,
  scale: 4,
  // Same rung as Scale on purpose: Hotel inherits every Scale feature. What makes it
  // different is an EXCLUSIVE entitlement, not a higher number — see ROOM_SERVICE_PLANS.
  hotel: 4,
  command: 5,
  control: 2,      // legacy alias for menu
  enterprise: 5,   // legacy alias for command
};

/**
 * Every tier, cheapest first, with the legacy aliases last.
 *
 * Exists because the plan LIST is what drifts, not the type. A `PlanTier` union is
 * checked by the compiler; a hand-written `['free', 'starter', …]` in a filter dropdown
 * is not, so a new tier silently becomes unfilterable — which is exactly what happened to
 * the admin portal's organization filter when Hotel was added. `plans.test.ts` asserts
 * this covers every key of `PLAN_ORDER`, so adding a tier without listing it fails.
 */
// `as const satisfies` rather than a `: readonly PlanTier[]` annotation. The annotation
// erased the literal types, so `z.enum(ALL_PLAN_TIERS)` did not compile and every zod
// schema that needed a plan enum typed the tiers out again by hand instead — three of
// them in the admin API alone, all of them missing Hotel. `satisfies` keeps the same
// compile-time check that every entry is a real `PlanTier`.
export const ALL_PLAN_TIERS = [
  'free', 'starter', 'menu', 'operate', 'scale', 'hotel', 'command',
  // Legacy aliases. They exist on old rows and must stay filterable, but nobody is sold
  // one — see SELLABLE_PLAN_TIERS.
  'control', 'enterprise',
] as const satisfies readonly PlanTier[];

/**
 * The tiers an operator may actually put an account on.
 *
 * The legacy aliases are excluded: `control` IS `menu` and `enterprise` IS `command`, so
 * offering them would create two names for one thing and make the plan mix unreadable.
 */
export const SELLABLE_PLAN_TIERS = [
  'free', 'starter', 'menu', 'operate', 'scale', 'hotel', 'command',
] as const satisfies readonly PlanTier[];

/**
 * The tiers someone actually PAYS for — everything sellable except Free.
 *
 * One list, because every use of it is the same question: Stripe checkout, an in-app
 * upgrade, an admin comp, and the starting plan on a newly provisioned org. All four had
 * written it out separately and all four were missing `hotel`, so on 2026-09-14 the
 * dashboard showed a Hotel plan card whose Upgrade button returned VALIDATION_ERROR, and
 * the admin portal could neither set nor comp a Hotel plan.
 *
 * `command` stays listed even though Commander is withdrawn from sale: the endpoints
 * have always accepted it and hiding the card is what closed it, not the schema.
 */
export const PAID_PLAN_TIERS = [
  'starter', 'menu', 'operate', 'scale', 'hotel', 'command',
] as const satisfies readonly PlanTier[];

export function getPlanTier(plan: PlanTier): number {
  return PLAN_ORDER[plan] ?? 0;
}

export function meetsMinPlan(userPlan: PlanTier, requiredPlan: PlanTier): boolean {
  return getPlanTier(userPlan) >= getPlanTier(requiredPlan);
}

/**
 * Is moving from `from` to `to` an upgrade?
 *
 * Not `PLAN_ORDER[to] > PLAN_ORDER[from]`, because Hotel and Scale deliberately share
 * rung 4 - Hotel is Scale plus in-room dining. Every place that asked the rank question
 * directly got the pair wrong in one direction or the other:
 *
 *   - the upgrade endpoint refused `4 <= 4` as a downgrade, so a Scale customer could not
 *     buy Hotel at all - the plan was fully built, priced and provisionable, and the one
 *     thing standing in the way was a `<=`;
 *   - the plan card called Hotel "lower tier" from Scale and disabled it;
 *   - the billing email announced Scale -> Hotel as a downgrade.
 *
 * One function, so the endpoint, the card and the email cannot disagree about which way a
 * plan change went.
 */
export function isPlanUpgrade(from: PlanTier, to: PlanTier): boolean {
  if (from === to) return false;
  const fromRank = PLAN_ORDER[from];
  const toRank = PLAN_ORDER[to];
  if (toRank !== fromRank) return toRank > fromRank;
  // Same rung. Two tiers only share one when one of them adds an exclusive entitlement,
  // so the tie breaks on that entitlement: gaining room service is an upgrade, losing it
  // is not, and a rung shared without one (a future alias) is neither.
  return ROOM_SERVICE_PLANS.includes(to) && !ROOM_SERVICE_PLANS.includes(from);
}

// ─── Payment processing ──────────────────────────────────────────────────────
// Online payment processing is BUILT-IN from the Operation tier up. The Menu
// tier (and its legacy 'control' alias) must purchase the Payment Processing
// add-on (`addon_payment`); Free has no ordering at all. This is the single
// source of truth — API guards, billing page, and guest gating all use it.
export const PAYMENTS_INCLUDED_MIN_PLAN: PlanTier = 'operate';

/** True when the plan tier includes online payments without the add-on (Operation+). */
export function planIncludesPayments(plan: PlanTier): boolean {
  return meetsMinPlan(plan, PAYMENTS_INCLUDED_MIN_PLAN);
}

/** Can this org accept online payments? Built-in for Operation+; the Menu tier
 *  needs the `addon_payment` add-on. */
export function hasPaymentProcessing(plan: PlanTier, activeAddons?: readonly string[] | null): boolean {
  return planIncludesPayments(plan) || (activeAddons ?? []).includes('addon_payment');
}

// ─── In-room dining (room service) ───────────────────────────────────────────
/**
 * Plans that include in-room dining.
 *
 * A LIST, not a rung, and that is the whole point. `meetsMinPlan` is a `>=` over
 * `PLAN_ORDER`, so a ladder cannot express "only this plan has it": putting Hotel level
 * with Scale would hand the feature to Scale, and putting it above would hand it to
 * Command. Neither is what was asked for.
 *
 * Hotel therefore shares Scale's rung — inheriting every Scale feature — and room service
 * is gated by membership of this list instead.
 */
export const ROOM_SERVICE_PLANS: readonly PlanTier[] = ['hotel'];

/** The add-on that grants in-room dining to an org on any other plan. */
export const ADDON_ROOM_SERVICE = 'addon_room_service';

/**
 * Can this org run in-room dining?
 *
 * Mirrors `hasPaymentProcessing` exactly: the plan, OR an add-on granted out of band. The
 * add-on is how an admin turns the feature on for a venue that is not on Hotel — a hotel
 * running the Menu plan, a pilot, an account mid-migration — without moving its plan or
 * changing its bill.
 *
 * `activeAddons` comes from `organizations.active_addons`.
 */
export function hasRoomService(plan: PlanTier, activeAddons?: readonly string[] | null): boolean {
  return ROOM_SERVICE_PLANS.includes(plan) || (activeAddons ?? []).includes(ADDON_ROOM_SERVICE);
}

// ─── Feature Flags ───────────────────────────────────────────────────────────
// Single source of truth for which features are available per plan.
// Dashboard sidebar, PlanGate, billing page, and API guards all reference this.

export type FeatureId =
  // Free (Visible) — Phase 0–4b ✅
  | 'venue_profile'
  | 'manual_menu'
  | 'qr_view_only'
  | 'public_profile'
  | 'staff_management'
  // Menu — Phase 5
  | 'ai_menu_import'
  | 'ai_image_generation'
  | 'ai_video_generation'
  | 'smart_qr'
  | 'translations'
  | 'menu_analytics'
  | 'ai_sales_agent'
  // Operate — Phase 6
  | 'floor_plan'
  | 'qr_ordering_payments'
  | 'kitchen_portal'
  | 'bar_portal'
  | 'cashier_portal'
  | 'financials'
  | 'tips'
  // Scale — Phase 7
  | 'bookings'
  | 'waitlist'
  | 'reviews'
  | 'promotions'
  | 'queue_management'
  // Command — Phase 8
  | 'multi_location'
  | 'advanced_analytics'
  | 'custom_integrations';

interface FeatureDefinition {
  /** Minimum plan required to access this feature */
  minPlan: PlanTier;
  /** Which build phase delivers this feature */
  phase: number;
  /** Is this feature fully built and enabled? */
  enabled: boolean;
  /** Human-readable label */
  label: string;
}

export const FEATURE_REGISTRY: Record<FeatureId, FeatureDefinition> = {
  // ── Free (Visible) — Done ──────────────────────────────────────────────────
  venue_profile:        { minPlan: 'free',    phase: 3, enabled: true,  label: 'Venue Profile' },
  manual_menu:          { minPlan: 'free',    phase: 4, enabled: true,  label: 'Manual Menu' },
  qr_view_only:        { minPlan: 'free',    phase: 4, enabled: true,  label: 'QR View-Only' },
  public_profile:       { minPlan: 'free',    phase: 4, enabled: true,  label: 'Public Profile' },
  staff_management:     { minPlan: 'menu',    phase: 3, enabled: true,  label: 'Staff Management' },

  // ── Menu — Phase 5 (next) ─────────────────────────────────────────────────
  ai_menu_import:       { minPlan: 'menu',    phase: 5, enabled: true,  label: 'AI Menu Import' },
  ai_image_generation:  { minPlan: 'menu',    phase: 5, enabled: true,  label: 'AI Image Generation' },
  ai_video_generation:  { minPlan: 'menu',    phase: 5, enabled: false, label: 'AI Video Generation' },
  smart_qr:             { minPlan: 'menu',    phase: 5, enabled: false, label: 'Smart QR Ordering' },
  translations:         { minPlan: 'menu',    phase: 5, enabled: true,  label: 'Multi-Language Translations' },
  menu_analytics:       { minPlan: 'menu',    phase: 5, enabled: true,  label: 'Menu Analytics' },
  ai_sales_agent:       { minPlan: 'menu',    phase: 5, enabled: false, label: 'AI Sales Agent' },

  // ── Operate — Phase 6 ─────────────────────────────────────────────────────
  floor_plan:           { minPlan: 'operate', phase: 6, enabled: true,  label: 'Floor Plan' },
  qr_ordering_payments: { minPlan: 'menu',    phase: 5, enabled: true,  label: 'QR Ordering + Payments' },
  kitchen_portal:       { minPlan: 'menu',    phase: 5, enabled: true,  label: 'Kitchen Portal' },
  bar_portal:           { minPlan: 'menu',    phase: 5, enabled: true,  label: 'Bar Portal' },
  cashier_portal:       { minPlan: 'menu',    phase: 5, enabled: true,  label: 'Cashier Portal' },
  financials:           { minPlan: 'menu',    phase: 5, enabled: true,  label: 'Financials' },
  tips:                 { minPlan: 'operate', phase: 6, enabled: true,  label: 'Tips' },

  // ── Scale — Phase 7 ───────────────────────────────────────────────────────
  bookings:             { minPlan: 'scale',   phase: 7, enabled: true,  label: 'Bookings' },
  waitlist:             { minPlan: 'scale',   phase: 7, enabled: true,  label: 'Waitlist' },
  // Operation, not Scale. Three places gated reviews and they did not agree: the guest
  // app let anyone on Operation+ submit one, this registry said Scale, and the dashboard
  // route and sidebar said Scale — so an Operation venue collected reviews its owner was
  // redirected away from, forever. Proven in the browser on 2026-09-14: on `operate` the
  // guest review form renders and /venues/:slug/reviews bounces to /billing.
  //
  // Operation is the side that wins because it is what the customer is sold: the pricing
  // table marks the review row true for Operation. This entry is now the ONLY definition
  // — the guest hook, the route guard and the sidebar all read it.
  reviews:              { minPlan: 'operate', phase: 7, enabled: true,  label: 'Reviews' },
  promotions:           { minPlan: 'scale',   phase: 7, enabled: true,  label: 'Promotions' },
  queue_management:     { minPlan: 'scale',   phase: 7, enabled: true,  label: 'Queue Management' },

  // ── Command — Phase 8 ─────────────────────────────────────────────────────
  multi_location:       { minPlan: 'command', phase: 8, enabled: false, label: 'Multi-Location Dashboard' },
  advanced_analytics:   { minPlan: 'command', phase: 8, enabled: false, label: 'Advanced Analytics' },
  custom_integrations:  { minPlan: 'command', phase: 8, enabled: false, label: 'Custom Integrations' },
};

/**
 * Check if a feature is accessible for a given plan.
 * Returns true only if the feature is enabled AND the user's plan meets the minimum.
 */
export function isFeatureAvailable(feature: FeatureId, userPlan: PlanTier): boolean {
  const def = FEATURE_REGISTRY[feature];
  return def.enabled && meetsMinPlan(userPlan, def.minPlan);
}

/**
 * Check if a feature is enabled (built) regardless of plan.
 */
export function isFeatureEnabled(feature: FeatureId): boolean {
  return FEATURE_REGISTRY[feature].enabled;
}

/**
 * Get all enabled features for a plan tier.
 */
export function getEnabledFeatures(userPlan: PlanTier): FeatureId[] {
  return (Object.keys(FEATURE_REGISTRY) as FeatureId[]).filter(
    (id) => isFeatureAvailable(id, userPlan),
  );
}

// ─── Plan metadata for billing/marketing ─────────────────────────────────────

export interface PlanInfo {
  id: PlanTier;
  name: string;
  /** AED per month — except yearly-only plans (Starter), where it's the yearly total. */
  priceAed: number;
  /** Is this plan's feature set fully built? */
  built: boolean;
}

/**
 * Name and build state for EVERY tier, keyed so the compiler demands a row.
 *
 * It was an array with six hand-written rows and no `hotel`, which is how a Hotel card
 * that renders the right name and the right price still showed "Coming Soon" with the
 * Upgrade button disabled: `isPlanBuilt` did a `.find()` on this list, missed, and the
 * `?? false` turned "I have never heard of this tier" into "this tier is not built yet".
 * The plan was purchasable end to end by then - the schema, the Stripe price ids and the
 * checkout all accepted it - and this single missing row was the only thing left saying
 * no, in the one place the customer actually clicks.
 *
 * The legacy aliases are listed because `isPlanBuilt` is asked about whatever a live org
 * is on, and old rows still say `control` / `enterprise`.
 *
 * Prices are NOT repeated here - they are read from PLAN_PRICING_AED, which the pricing
 * file already calls the single source of truth. A second copy of a price is how a
 * trial-ending email came to quote a Hotel customer the Commander rate.
 */
const PLAN_CATALOGUE: Record<PlanTier, { name: string; built: boolean }> = {
  free:       { name: 'Visible',   built: true },
  starter:    { name: 'Starter',   built: true },
  menu:       { name: 'Menu',      built: true },
  operate:    { name: 'Operation', built: true },
  scale:      { name: 'Scale',     built: true },
  hotel:      { name: 'Hotel',     built: true },
  command:    { name: 'Commander', built: false },
  control:    { name: 'Menu',      built: true },   // legacy alias for menu
  enterprise: { name: 'Commander', built: false },  // legacy alias for command
};

/**
 * The tiers a plan picker shows, cheapest first. Derived, so it cannot fall behind.
 *
 * `priceAed` is per month, except on a yearly-only plan (Starter) where it is the annual
 * total — there is no monthly figure to show and 0 would read as "free".
 */
export const PLAN_INFO: PlanInfo[] = SELLABLE_PLAN_TIERS.map((id) => {
  const pricing = PLAN_PRICING_AED[id];
  return {
    id,
    name: PLAN_CATALOGUE[id].name,
    priceAed: pricing.yearlyOnly ? pricing.yearlyAed : pricing.monthlyAed,
    built: PLAN_CATALOGUE[id].built,
  };
});

// ─── AI Credits per plan (monthly allocation) ───────────────────────────────
// Re-export from ai-credits.ts (single source of truth)

import { PLAN_MONTHLY_CREDITS } from './ai-credits';

/** @deprecated Use PLAN_MONTHLY_CREDITS from ai-credits.ts */
export const PLAN_AI_CREDITS = PLAN_MONTHLY_CREDITS;

export function getPlanAiCredits(plan: string): number {
  // `plan` is a bare string here (this is the deprecated accessor), so it is checked
  // against the real tiers rather than indexed blind — an unknown tier must read as 0,
  // not crash, but a REAL tier must never read as 0 just because the map forgot it.
  return PLAN_MONTHLY_CREDITS[plan as PlanTier] ?? 0;
}

/**
 * Is a given plan fully built and available for upgrade?
 * Used by billing page to disable upgrade buttons for unbuilt plans.
 */
export function isPlanBuilt(planId: string): boolean {
  // Reads the CATALOGUE, not the sellable list: a live org can be on a legacy alias, and
  // answering "not built" for the plan someone is currently paying for disables their own
  // card. An id that is not a tier at all is still false.
  return PLAN_CATALOGUE[planId as PlanTier]?.built ?? false;
}
